<template>
  <Modal
    ref="addImageModalRef"
    content-id="add-image-modal"
    :clickToClose="false"
    :showIcon="false"
    :modalStyle="{ maxWidth: 'calc(100vw - 100px)', width: '928px' }"
    :outsideScrollDisabled="true"
    :withCloser="false"
    @modalClosed="onModalClosed"
  >
    <template #body>
      <div class="title-container">
        <h3 class="title">Görsel Ekle</h3>
        <div class="modal-close-icon" @click="closeModal"></div>
      </div>
      <div v-if="isFileSelectMode">
        <div class="product-option">
          <div>
            Ürün Seçeneği:
            <span>
              {{ productOptionName }}
            </span>
          </div>
          <span class="pipe">|</span>
          <div class="image-counter">
            <span> {{ uploadedCount }} / {{ maxImages }} </span>
            görsel yüklendi
          </div>
          <div
            v-if="failedCount"
            class="fail-warning text-red-600 flex items-center ml-6"
          >
            <img src="@/assets/images/icons/alert-circle.svg" />
            <span>{{ failedCount }} görsel yüklenemedi!</span>
          </div>
        </div>
        <div class="image-grid">
          <div
            v-for="(image, index) in images"
            :key="index"
            :draggable="!!image.file && uploadedCount > 1"
            @dragstart="image.file && onDragStart(index)"
            @dragover.prevent="image.file && onDragOver(index)"
            @drop.prevent="image.file && onDrop(index)"
          >
            <div class="image-box">
              <button
                v-if="image.file"
                class="remove-image"
                @click="removeImage(index)"
              >
                <Icons name="Delete" color="#FF44EE" />
              </button>
              <img
                v-if="image.preview"
                :src="image.preview"
                class="image-preview"
                alt="preview"
              />
              <img
                v-else-if="image.status === 'error'"
                src="@/assets/images/icons/default-product-image.svg"
                class="image-preview"
                alt="Default image"
              />
              <div v-if="image.file" class="image-info">
                <div class="image-details">
                  <div class="file-name">{{ image.file.name }}</div>
                  <div class="status">
                    <div
                      v-if="image.status === 'uploaded'"
                      class="uploaded text-green-600 flex items-center"
                    >
                      Yüklendi
                    </div>
                    <div
                      v-else-if="image.status === 'error'"
                      class="error text-red-600"
                    >
                      <div class="error-icon">
                        <img src="@/assets/images/icons/alert-circle.svg" />
                        Yüklenemedi
                      </div>
                      <span v-if="image.errorType === 'format'">
                        Dosya uzantısı <strong>.jpeg, .jpg, .png</strong> veya <strong>.webp</strong> formatında olmalıdır.
                      </span>
                      <span v-else>{{ getErrorMessage(image.errorType) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                <template v-if="index === nextEmptySlotIndex">
                  <button
                    class="next-empty-placeholder"
                    @click="handlePlaceholderClick(index)"
                    @dragover.prevent
                    v-if="index === nextEmptySlotIndex"
                    @drop.prevent="handlePlaceholderDrop(index, $event)"
                  >
                    <img
                      class="plus-icon"
                      src="@/assets/images/icons/plus.svg"
                    />
                    <span class="placeholder-text">Görsel ekle</span>
                    <span class="placeholder-subtext">
                      Bilgisayardan seçin veya sürükleyip bırakın.
                    </span>
                  </button>
                  <div v-else class="empty-placeholder"></div>
                </template>
              </div>
              <div
                class="cover-button"
                v-if="image.file && (index === 0 || loadedImageCount === 1)"
              >
                Kapak Görseli
              </div>
            </div>
            <button v-if="image.file && uploadedCount > 1" class="move-button">
              <img src="@/assets/images/move-dot.svg" alt="" />
            </button>
          </div>
        </div>
        <p class="drag-hint">
          <img src="@/assets/images/info.svg" />
          Görsellerin yerini değiştirmek için sürükleyip bırakın.
        </p>
      </div>

      <div v-else>
        <div
          class="template-box droppable upload"
          @dragover.prevent
          @drop.prevent="onBulkDrop"
        >
          <div class="template-inner">
            <div class="content">
              <div class="icon-container">
                <img
                  src="@/assets/images/image-upload@2x.png"
                  class="icon-img"
                />
              </div>
              <h2 class="action-title">Görsel Ekle</h2>
              <div>
                <p class="action-description">
                  Ürün görsellerini yüklemek için bilgisayarınızdan seçin veya
                  buraya sürükleyip bırakın.
                </p>
                <span class="action-description-note"
                  >(En fazla 8 görsel yükleyebilirsiniz.)</span
                >
              </div>
              <div v-if="selectedFile" class="file-info">
                <button class="remove-file" @click="removeSelectedFile">
                  ×
                </button>
                <div class="file-details-container">
                  <img
                    src="@/assets/images/xls-file.svg"
                    alt="Icon"
                    class="icon"
                  />
                  <div class="file-details">
                    <div class="file-name">{{ selectedFile.name }}</div>
                    <div class="file-size">{{ formattedFileSize }}</div>
                  </div>
                </div>
              </div>
              <AppButton
                title="Dosya Seç"
                primary
                md
                class="w-auto"
                @click="openFileBrowser"
              />
              <p class="file-restrictions" v-if="!selectedFile">
                Görseller <strong>.jpeg, .jpg, .png</strong> veya <strong>.webp</strong> formatında ve en fazla <strong>10 MB</strong> boyutunda olmalıdır.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="file-input-container">
        <input
          ref="fileInput"
          type="file"
          @change="onFileChange"
          hidden
          accept="image/jpeg,image/png,image/jpg,image/webp"
          multiple
        />
      </div>

      <div class="d-flex bottom-container">
        <AppButton
          title="Vazgeç"
          secondary
          lg
          class="w-full"
          @click="cancelAction"
        />
        <AppButton
          v-if="!isEditing"
          title="Ekle"
          primary
          lg
          class="w-full"
          :disabled="!allUploaded"
          @click="confirmAction"
        />
        <AppButton
          v-else
          title="Kaydet"
          primary
          lg
          class="w-full"
          @click="confirmAction"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { Modal, AppButton, Icons } from "@n11/so-ui-components";
import { SINGULAR_PRODUCT_TABLE_IMAGE_UPLOAD_ERROR_MESSAGES } from "@/utils/enum/tableColumns";

const store = useStore();

const emit = defineEmits(["images-uploaded"]);

const props = defineProps({
  initialImages: {
    type: Array,
    default: () => [],
  },
  rowIds: {
    type: Array,
    default: () => [],
  },
  allRows: {
    type: Array,
    default: () => [],
  },
});

const maxImages = 8;
const productOptionName = computed(() => {
  const rowId = props.rowIds[0];
  const current = props.allRows.find((r) => r.id === rowId);
  if (!current) return "";
  const colorAttr = current.attributes.find((a) => a.name === "Renk");
  return colorAttr?.value || "";
});
const isFileSelectMode = ref(false);
const images = ref([]);
const selectedPlaceholderIndex = ref(null);
const coverIndex = ref(null);
const addImageModalRef = ref(null);
const fileInput = ref(null);
const selectedFile = ref(null);
const dragSourceIndex = ref(null);
const dragOverIndex = ref(null);
const isEditing = computed(() => props.initialImages.length > 0);
const deletedItems = ref([]);

const errorMessages = SINGULAR_PRODUCT_TABLE_IMAGE_UPLOAD_ERROR_MESSAGES;

const allUploaded = computed(() => {
  const imagesHasFile = images.value.filter((img) => img.file);
  if (imagesHasFile.length === 0) return false;
  return imagesHasFile.every((img) => img.status === "uploaded");
});

const formattedFileSize = computed(() =>
  selectedFile.value
    ? (selectedFile.value.size / 1024 / 1024).toFixed(2) + " MB"
    : ""
);

const uploadedCount = computed(
  () => images.value.filter((img) => img.status === "uploaded").length
);

const failedCount = computed(
  () => images.value.filter((img) => img.status === "error").length
);

const nextEmptySlotIndex = computed(() =>
  images.value.findIndex((img) => !img.file)
);

const getErrorMessage = (type) => errorMessages[type] || "Yüklenemedi.";

const initializeImages = (list) => {
  images.value = Array.from({ length: maxImages }, (_, i) => {
    const img = list.find((x) => x.order === i);
    if (img) {
      return {
        file: img,
        preview: img.url,
        status: "uploaded",
        errorType: null,
        id: img.id,
        order: img.order,
      };
    }
    return { file: null, preview: "", status: null, errorType: null };
  });
};

const openAddImageModal = () => {
  initializeImages(props.initialImages);
  isFileSelectMode.value = props.initialImages.length > 0;
  addImageModalRef.value.openModal();
};

const closeModal = () => {
  addImageModalRef.value.hiddenModal();
};

const onModalClosed = () => {
  store.commit("products/SET_SINGLE_PRODUCT_IMAGE_UPLOAD_ROW_ID", null);
};

const openFileBrowser = () => {
  fileInput.value && fileInput.value.click();
};

const handlePlaceholderClick = (index) => {
  selectedPlaceholderIndex.value = index;
  openFileBrowser();
};

const handlePlaceholderDrop = (index, event) => {
  const files = event.dataTransfer.files;
  if (files && files.length > 0) {
    isFileSelectMode.value = true;
    handleFiles(files, index);
  }
};

const onFileChange = (event) => {
  const files = event.target.files;
  if (files && files.length > 0) {
    isFileSelectMode.value = true;
    handleFiles(files, selectedPlaceholderIndex.value);
    selectedPlaceholderIndex.value = null;
  }
};

const onDragStart = (index) => {
  dragSourceIndex.value = index;
};

const onDragOver = (index) => {
  dragOverIndex.value = index;
};

const onDrop = (targetIndex) => {
  if (!images.value[targetIndex].file) return;
  const sourceIndex = dragSourceIndex.value;
  if (sourceIndex === targetIndex || !images.value[sourceIndex].file) return;
  const temp = images.value[targetIndex];
  images.value[targetIndex] = images.value[sourceIndex];
  images.value[sourceIndex] = temp;
  dragSourceIndex.value = null;
};

const handleFiles = (fileList, dropIndex = null) => {
  const allowedExt = ["jpg", "jpeg", "png", "webp"];
  const sizeLimit = 10 * 1024 * 1024;
  const dimLimit = 5000;
  let nextSlot =
    dropIndex != null ? dropIndex : images.value.findIndex((img) => !img.file);

  Array.from(fileList).forEach((file) => {
    if (nextSlot < 0 || nextSlot >= maxImages) return;

    images.value[nextSlot] = {
      file,
      preview: "",
      status: "loading",
      errorType: null,
    };

    const ext = file.name.split(".").pop().toLowerCase();
    if (!allowedExt.includes(ext)) {
      images.value[nextSlot].status = "error";
      images.value[nextSlot].errorType = "format";
      nextSlot++;
      return;
    }

    if (file.size > sizeLimit) {
      images.value[nextSlot].status = "error";
      images.value[nextSlot].errorType = "size";
      nextSlot++;
      return;
    }

    const slotIndex = nextSlot;
    const reader = new FileReader();
    reader.onload = (e) => {
      const imgEl = new Image();
      imgEl.onload = () => {
        if (imgEl.width > dimLimit || imgEl.height > dimLimit) {
          images.value[slotIndex].status = "error";
          images.value[slotIndex].errorType = "dimension";
        } else {
          images.value[slotIndex].preview = e.target.result;
          images.value[slotIndex].status = "uploaded";
        }
      };
      imgEl.src = e.target.result;
    };
    reader.readAsDataURL(file);

    nextSlot++;
  });
};

const removeSelectedFile = () => {
  selectedFile.value = null;
};

const removeImage = (index) => {
  deletedItems.value.push(images.value[index]);
  images.value.splice(index, 1);
  images.value.push({ file: null, preview: "", status: null });
  if (coverIndex.value === index) {
    coverIndex.value = null;
  }
};

const cancelAction = () => {
  store.commit("products/SET_SINGLE_PRODUCT_IMAGE_UPLOAD_ROW_ID", null);
  closeModal();
};

const onBulkDrop = (event) => {
  const files = event.dataTransfer.files;
  if (files && files.length) {
    isFileSelectMode.value = true;
    handleFiles(files);
  }
};

const confirmAction = async () => {
  const newSlots = images.value.filter((img) => img.file && !img.id);

  let uploaded = [];
  if (newSlots.length) {
    const formData = new FormData();
    newSlots.forEach((slot) => formData.append("images", slot.file));
    try {
      uploaded = await store.dispatch("products/uploadProductImages", {
        productImages: formData,
      });
    } catch (err) {
      console.error("Upload hatası", err);
    }
  }

  const newPayload = uploaded
    .filter((u) => u.success)
    .map((u, i) => {
      const slotIndex = images.value.indexOf(newSlots[i]);
      return { id: u.id, url: u.url, order: slotIndex };
    });

  const finalImages = images.value
    .map((slot, idx) => {
      if (slot.id) {
        return { id: slot.id, url: slot.preview, order: idx };
      }
      const match = newPayload.find((p) => p.order === idx);
      return match || null;
    })
    .filter(Boolean);

  emit("images-uploaded", {
    rowIds: props.rowIds,
    images: finalImages,
  });

  deletedItems.value = [];
  closeModal();
};

defineExpose({
  openAddImageModal,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 12px;

  .action-description :deep(u) {
    font-weight: 600;
    color: $colorM50;
    text-decoration: none;
  }

  .action-title {
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
  }
}

.title-container {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}
.title {
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 26px; /* 130% */
  letter-spacing: -0.2px;
}

.modal-close-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
  margin-left: auto;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3Cpath stroke='%237A8085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='m8 8 8 8-4-4 4-4-8 8'/%3E%3Cpath d='M16.38 6.214c.91-.754 2.16.496 1.406 1.406l-.079.087L13.415 12l4.292 4.293c.914.914-.388 2.272-1.327 1.493l-.087-.079L12 13.415l-4.293 4.292a1 1 0 0 1-1.32.083l-.094-.083a1 1 0 0 1-.083-1.32l.083-.094L10.585 12 6.293 7.707a1 1 0 0 1-.083-1.32l.083-.094a1 1 0 0 1 1.32-.083l.094.083L12 10.585l4.293-4.292.087-.08z' fill='%23707070'/%3E%3C/g%3E%3C/svg%3E%0A");
}

/* --- Orijinal yapı (Dosya seçilmediğinde) --- */
.template-box {
  border: 2px solid var(--Red--Corporate-R50);
  border-radius: 12px;
  width: 100%;
  text-align: center;
  position: relative;
  transition: all 150ms ease-in-out;
  margin-bottom: 16px;
  &.upload {
    border-color: transparent;
    &:before {
      content: "";
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23FF44EE' stroke-width='5' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
      background-position: center center;
      border-radius: 12px;
      left: -2px;
      top: -2px;
      right: -2px;
      bottom: -2px;
      position: absolute;
    }
  }
}
.product-option {
  display: flex;
  margin-bottom: 16px;
  font-weight: 400;
  font-size: 13px;
  color: $colorN80;
  gap: 16px;
  align-items: center;

  .image-counter {
    font-size: 13px;
  }

  span {
    font-weight: 600;

    &.pipe {
      color: $colorD30;
      font-weight: 400;
    }
  }

  .fail-warning {
    display: flex;
    flex-direction: row;
    font-size: 12px;
    line-height: 16px;
    color: $colorE60;
    gap: 4px;

    img {
      width: 16px;
      height: auto;
    }
  }
}

/* Grid düzeni (4 sütun) */
.image-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

/* Her bir görsel kutusu */
.image-box {
  position: relative;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  height: 240px; /* Görsel + yazı + butonlar rahat sığsın diye */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  max-width: 99px;
  width: 100%;

  &:has(.next-empty-placeholder) {
    border: 1px dashed $colorM50;
    justify-content: center;
    > div {
      height: 100%;
    }
  }

  &:has(.empty-placeholder) {
    background-color: $colorD05;
  }

  &:has(.cover-button) {
    border-color: $colorM50;
  }

  /*
  &.drag-over {
    border-color: #5d3ebc;
  }
  */
}

.next-empty-placeholder {
  display: flex;
  flex-direction: column;
  padding: 8px;
  justify-content: center;
  align-items: center;
  background-color: $colorM05;
  gap: 4px;
  height: 100%;
  border-radius: 8px;

  .placeholder-text {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    color: $colorM50;
  }

  .placeholder-subtext {
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
  }
  .plus-icon {
    border-radius: 100%;
  }
}

.move-button {
  margin-top: 16px;
}

.image-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 8px;
}

.image-preview {
  max-width: 100%;
  max-height: 141px;
  object-fit: contain;
  height: 100%;
  border-radius: 8px 8px 0px 0px;
}

.image-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  .file-name {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 4px;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 83px;
    line-height: 14px;
  }
  .status {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.cover-button {
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 0px 0px 8px 8px;
  color: #fff;
  background-color: $colorN80;
  border-color: $colorN80;
  width: 100%;
  position: absolute;
  bottom: -1px;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
}

.uploaded {
  color: $colorS50;
  display: flex;
  align-items: center;
  font-size: 10px;
  gap: 4px;
  font-style: normal;
  font-weight: 600;
  &:before {
    content: "";
    width: 12px;
    display: block;
    height: 12px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Ccircle cx='6' cy='6' r='6' fill='%23008C20'/%3E%3Cpath d='M8.39961 4.2002L5.39961 7.8002L3.59961 6.0002' stroke='white' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  }
}

.uploading {
  color: #ffa500;
  font-size: 12px;
}
.error {
  color: red;
  font-size: 10px;
  font-weight: 400;
  text-align: left;
  &-icon {
    display: flex;
    gap: 4px;
    margin-bottom: 4px;
    align-items: center;
    img {
      width: 12px;
    }
  }
}

.drag-hint {
  font-size: 13px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 3px;
  img {
    width: 24px;
    height: 24px;
  }
}

.template-inner {
  border-radius: 12px;
  padding: 24px;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $colorM05;
  z-index: 10;
  position: relative;
}
.action-description {
  font-size: 14px;
  color: #333;
}
.action-description-note {
  font-weight: bold;
  font-size: 13px;
  margin-top: 4px;
  margin-bottom: 10px;
}

.file-restrictions {
  font-size: 12px;
  margin-top: 16px;
}
strong {
  font-weight: bold;
}

.icon-container {
  background-color: #e1d7ff;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
}

.icon-img {
  width: 90px;
  height: 90px;
}

.remove-image {
  position: absolute;
  top: 4px;
  right: 4px;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 6px;
  border: 0.5px solid $colorD20;
  background-color: white;
  border-radius: 50px;
}

.bottom-container {
  margin-top: -12px;
  gap: 8px;
}
</style>
